import { SurveyValidator, ValidatorResult } from "survey-core";
import { normalisePostalCode } from "../helpers/normalisePostalCode";
import { getPostalCodeRule } from "./postalCodeRules";

/**
 * A custom SurveyJS validator that validates a postal code against a
 * country-specific rule. It keeps the postal-code regex in a single place
 * (see {@link getPostalCodeRule}) instead of duplicating it across every form.
 *
 * Usage in a survey form's `validators` array:
 * ```json
 * { "type": "postalcode", "country": "NL" }
 * ```
 *
 * The class name is `postalcodevalidator`; SurveyJS derives the JSON `type`
 * ("postalcode") by stripping the trailing "validator". The validator is
 * registered in `registrations/registerValidators.ts`.
 */
export class PostalCodeValidator extends SurveyValidator {
  getType(): string {
    return "postalcodevalidator";
  }

  get country(): string {
    return this.getPropertyValue("country");
  }
  set country(val: string) {
    this.setPropertyValue("country", val);
  }

  validate(value: unknown, name?: string): ValidatorResult | null {
    // Let required-validation handle empty values; only validate the format.
    if (value === undefined || value === null || value === "") {
      return null;
    }

    const rule = getPostalCodeRule(this.country);
    // Unknown country: no rule to enforce, so do not block the respondent.
    if (!rule) {
      return null;
    }

    const normalised = normalisePostalCode(String(value));
    if (rule.regex.test(normalised)) {
      return null;
    }

    return new ValidatorResult(value, this.createCustomError(name ?? ""));
  }

  protected getDefaultErrorText(_name: string): string {
    const rule = getPostalCodeRule(this.country);
    const example = rule?.example ?? "";
    return `Voer een geldige postcode in (bijv. ${example})`;
  }
}
